import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { VisitorAuthProvider } from "@/context/VisitorAuthContext";
import { LangProvider } from "@/lib/i18n";
import { initPixels } from "@/lib/analytics";

import HomePage from "@/pages/HomePage";
import GetStartedPage from "@/pages/GetStartedPage";
import ThankYouPage from "@/pages/ThankYouPage";
import OpportunityPage from "@/pages/OpportunityPage";
import AdLandingPage from "@/pages/AdLandingPage";
import ReviewRedirect from "@/pages/ReviewRedirect";
import ReferralRedirect from "@/pages/ReferralRedirect";
import SharePage from "@/pages/SharePage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ChatWidget from "@/components/ChatWidget";
import SignInPage from "@/pages/SignInPage";
import VisitorAuthCallback from "@/pages/VisitorAuthCallback";
import MyAccountPage from "@/pages/MyAccountPage";
import CompleteProfilePage from "@/pages/CompleteProfilePage";
import {
    HomeownersPage, RentersPage, MovingPage, FirstTimePage, ExistingPage, BusinessPage,
    TexasPage, HoustonPage, FixedRatePage,
} from "@/pages/segments";
import { PrivacyPage, TermsPage } from "@/pages/legal";

function App() {
    useEffect(() => {
        initPixels();
    }, []);
    return (
        <LangProvider>
            <AuthProvider>
                <VisitorAuthProvider>
                    <BrowserRouter>
                        <Toaster theme="dark" richColors position="top-right" />
                        <AppRoutes />
                        <ChatWidget />
                    </BrowserRouter>
                </VisitorAuthProvider>
            </AuthProvider>
        </LangProvider>
    );
}

function AppRoutes() {
    const location = useLocation();
    // Emergent OAuth returns to the redirect URL with #session_id=... — handle synchronously
    // before any other route renders, so AuthProvider can't race the exchange.
    if (location.hash?.includes("session_id=")) {
        return <VisitorAuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/opportunity" element={<OpportunityPage />} />
            <Route path="/lp/:channel" element={<AdLandingPage />} />
            <Route path="/review-us" element={<ReviewRedirect />} />
            <Route path="/ref/:code" element={<ReferralRedirect />} />
            <Route path="/share/:code" element={<SharePage />} />
            <Route path="/homeowners" element={<HomeownersPage />} />
            <Route path="/renters" element={<RentersPage />} />
            <Route path="/moving" element={<MovingPage />} />
            <Route path="/first-time" element={<FirstTimePage />} />
            <Route path="/existing" element={<ExistingPage />} />
            <Route path="/business" element={<BusinessPage />} />
            <Route path="/compare-texas" element={<TexasPage />} />
            <Route path="/compare-houston" element={<HoustonPage />} />
            <Route path="/fixed-rate" element={<FixedRatePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/my-account" element={<MyAccountPage />} />
            <Route path="/complete-profile" element={<CompleteProfilePage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<HomePage />} />
        </Routes>
    );
}

export default App;
